import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";

type Variant = "primary" | "secondary" | "outline" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

/**
 * The primary action is near-black, not the accent colour.
 *
 * This is one of the most distinctive things about the design language and the
 * easiest to get wrong: reaching for the brand colour on every call-to-action is
 * the default instinct, and it makes the interface look like every other
 * template. The accent is reserved for navigation state, focus, links and
 * highlights.
 */
const variantStyles: Record<Variant, string> = {
  primary: "bg-primary text-primary-foreground shadow-sm hover:bg-ink-800 active:bg-ink-950",
  secondary: "bg-accent-500 text-accent-foreground shadow-accent hover:bg-accent-600 active:bg-accent-700",
  outline: "border border-line bg-card text-ink-800 shadow-xs hover:bg-ink-50 hover:border-line-strong active:bg-ink-100",
  ghost: "bg-transparent text-ink-600 hover:bg-ink-100 hover:text-ink-900 active:bg-ink-150",
  danger: "bg-danger text-white shadow-sm hover:bg-red-700 active:bg-red-800",
};

/**
 * `sm` is 36px rather than the 32px a small button usually gets: below roughly
 * 40px, a touch target on a counter tablet becomes genuinely hard to hit.
 */
const sizeStyles: Record<Size, string> = {
  sm: "h-9 px-3.5 text-xs gap-1.5",
  md: "h-10 px-[1.125rem] text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading = false, icon, className = "", children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      // The transition enumerates properties rather than using `transition-all`,
      // which would also animate `color` and make label changes smear.
      className={`inline-flex select-none items-center justify-center whitespace-nowrap rounded-lg font-medium
        transition-[background-color,border-color,box-shadow,transform] duration-150 ease-out
        active:scale-[0.98] motion-reduce:active:scale-100
        disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none disabled:active:scale-100
        ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
      {...props}
    >
      {/* Loading replaces the icon and keeps the label, so the button does not
          change width mid-action and shift everything beside it. */}
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
});
